import { StandardButton } from 'base/StandardButton/StandardButton';
import SearchBox from 'components/common/SearchBox';
import { t } from 'i18next';
import React, { useEffect, useState, useCallback } from 'react';
import { Dropdown } from 'react-bootstrap';
import { capitalizeFirstLetter, checkAccess, showValue } from 'utils/helpers';
import { useAppDispatch } from '../../redux/HooksRedux';
import * as userAction from '../../redux/user/userAction';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { UserPageInterfaceI, paramsI } from '../../../types/interface';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import CustomAdvanceTableFooter from 'components/customComponent/CustomAdvanceTableFooter';
import { useNavigate } from 'react-router-dom';
import { USER_FORM, USER_FORM_ID } from 'constant/RoutesUrls';
import { RootState } from 'redux/store';
import { errorNotification } from 'base/notifications/Notifications';
import { useSelector } from 'react-redux';
import { debounceTime, user_type } from 'utils/staticData';
import FeatherIcon from 'feather-icons-react';
import { StandardLoader } from 'base/Loader/Loader';
import './User.scss';
import {
  ACCENDING,
  ACCESS_TOKEN,
  DESCENDING,
  modules,
  ViewAllLimit
} from 'utils/enums/misc';
import {
  clearSubDomainCookie,
  getCookie,
  getDomainFromCurrentUrl
} from 'utils/cookieUtils';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import StandardSelect from 'base/StandardSelect/StandardSelect';

const User = () => {
  const navigate = useNavigate();
  const { totalPages, totalRecords } = useSelector(
    (state: RootState) => state?.userDataSlice || 0
  );
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );
  const dispatch = useAppDispatch();

  const [userList, setUserList] = useState<UserPageInterfaceI[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageCount, setPageCount] = useState<number>(1);
  const [searchText, setSearchText] = useState('');
  const [isAllVisibleData, setIsAllVisibleData] = useState(false);
  const [sorting, setSorting] = useState<SortingState>();
  const [limit, setLimit] = useState<number>(10);
  const [showImpersonateModal, setShowImpersonateModal] = useState<{
    user_id: number;
    show: boolean;
  }>({ user_id: 0, show: false });
  const [selectedUserType, setSelectedUserType] = useState<string>('');
  console.log(process.env.REACT_APP_API_URL);
  const getAllUsers = async () => {
    const params: paramsI & { user_type?: string } = {
      page: pageCount,
      limit: isAllVisibleData ? ViewAllLimit : limit,
      keyword: searchText.trim().length > 2 ? searchText : '',
      sortBy: sorting?.[0]?.id,
      sortOrder: sorting?.[0]?.desc ? DESCENDING : ACCENDING,
      user_type: selectedUserType || undefined
    };

    setIsLoading(true);

    try {
      const response = await dispatch(userAction.getAllUsers(params));
      if (response?.status === 200) {
        setUserList(response.data?.data || []);
        if (pageCount > response?.data?.meta?.totalPages) {
          setPageCount(1);
        }
      } else {
        errorNotification(response?.data?.message || 'An error occurred');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(() => {
    const handler = setTimeout(() => {
      if (searchText.length === 0 || searchText.length > 2) {
        getAllUsers();
      }
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [
    searchText,
    pageCount,
    isAllVisibleData,
    sorting,
    limit,
    selectedUserType
  ]);

  useEffect(() => {
    if (searchText.length === 0 || searchText.length > 2) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
    const cleanup = debouncedSearch();
    return cleanup;
  }, [
    searchText,
    pageCount,
    isAllVisibleData,
    sorting,
    limit,
    selectedUserType
  ]);

  const impersonateUser = (userId: number) => {
    const token = getCookie(ACCESS_TOKEN);
    const domain = process.env.REACT_APP_DOMAIN;
    const subDomain = process.env.REACT_APP_SUB_DOMAIN;
    const date = new Date();
    const expireDate = 'Thu, 01 Jan 1970 00:00:00 UTC';
    date.setTime(date.getTime() + 1000 * 60 * 60 * 6);

    const domainFromCurrent = getDomainFromCurrentUrl();

    clearSubDomainCookie('AuthToken', { domain: domainFromCurrent });
    clearSubDomainCookie('AuthToken', {
      domain: `${subDomain}${domainFromCurrent}`
    });
    clearSubDomainCookie('i_user_id', { domain: domainFromCurrent });
    clearSubDomainCookie('i_user_id', {
      domain: `${subDomain}${domainFromCurrent}`
    });

    document.cookie = `i_user_id=${userId}; domain=${domain}; path=/; expires=${date} secure=true`;
    document.cookie = `AuthToken=${token}; domain=${domain}; path=/; expires=${date} secure=true`;
    setShowImpersonateModal({ user_id: 0, show: false });
    // window.location.href = `https://${subDomain}${domain}/user`;
    window.open(`https://${subDomain}${domain}/user`, '_blank');
  };

  const columns: ColumnDef<UserPageInterfaceI>[] = [
    {
      id: 'user_number',
      accessorKey: 'user_number',
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_29')}</span>
      ),
      cell: ({ row: { original } }) => <div className="py-2">{showValue(original.user_number)}</div>
    },
    {
      id: 'first_name',
      accessorKey: t('general.common_text.text_5'),
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_5')}</span>
      ),
      cell: ({ row: { original } }) => showValue(original.first_name)
    },
    {
      id: 'last_name',
      accessorKey: t('general.common_text.text_6'),
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_6')}</span>
      ),
      cell: ({ row: { original } }) => showValue(original.last_name)
    },
    {
      id: 'email',
      accessorKey: t('general.common_text.text_4'),
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_4')}</span>
      ),
      cell: ({ row: { original } }) =>
        original.email?.toLocaleLowerCase() || '--'
    },
    {
      id: 'role',
      accessorKey: t('header.text.text_8'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('header.text.text_8')}</span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <div className="text-capitalize">
            {showValue(original?.role?.role_name)}
          </div>
        );
      }
    },
    {
      id: 'user_type',
      accessorKey: t('user.table.title_7'),
      header: () => (
        <span className="text-nowrap">{t('user.table.title_7')}</span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <ConditionalRender
            condition={original?.role?.role_name?.toLowerCase() === 'user'}
            fallBack={'--'}
          >
            <div className="text-capitalize">
              {capitalizeFirstLetter(
                user_type.filter(type => type?.value === original?.user_type)[0]
                  ?.label as string
              )}
            </div>
          </ConditionalRender>
        );
      }
    },
    {
      id: 'action',
      accessorKey: checkAccess([modules.USERS.edit], userPermission)
        ? t('general.common_text.text_8')
        : '',
      enableSorting: false,
      header: () =>
        checkAccess(
          [modules.USERS.edit, modules.USERS.view],
          userPermission
        ) ? (
          <span className="text-nowrap">{t('general.common_text.text_8')}</span>
        ) : (
          ''
        ),
      cell: ({ row: { original } }) => (
        <ConditionalRender
          condition={checkAccess(
            [modules.USERS.edit, modules.USERS.view],
            userPermission
          )}
        >
          <Dropdown
            id="scroll_in_table"
            placement="auto"
            className="position-static"
          >
            <Dropdown.Toggle
              variant="link"
              className="no-padding no-margin ps-0 d-flex"
            >
              <FeatherIcon icon="more-vertical" className="text-body" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-0">
              <ConditionalRender
                condition={checkAccess([modules.USERS.edit], userPermission)}
              >
                <Dropdown.Item
                  className="pe-0"
                  onClick={() =>
                    navigate(
                      `${USER_FORM_ID.replace(
                        ':userId',
                        original?.user_id?.toString()
                      )}`
                    )
                  }
                >
                  <span className="d-flex fs-16 p-0 font-normal">
                    <p className="mb-0">
                      {capitalizeFirstLetter(t('general.common_text.text_13'))}
                    </p>
                  </span>
                </Dropdown.Item>
              </ConditionalRender>
              <ConditionalRender
                condition={
                  checkAccess([modules.USERS.view], userPermission) &&
                  original.account_type?.toLowerCase() === 'user' &&
                  original.is_verified
                }
              >
                <Dropdown.Item
                  className="pe-0"
                  onClick={() =>
                    setShowImpersonateModal({
                      user_id: original?.user_id,
                      show: true
                    })
                  }
                >
                  <span className="d-flex fs-16 p-0 font-normal">
                    <p className="mb-0 pe-3">
                      {capitalizeFirstLetter(t('user.button.text_2'))}
                    </p>
                  </span>
                </Dropdown.Item>
              </ConditionalRender>
            </Dropdown.Menu>
          </Dropdown>
        </ConditionalRender>
      )
    }
  ];

  const table = useAdvanceTable({
    data: userList!,
    columns,
    selection: false,
    sortable: true,
    pageSize: limit,
    pagination: true,
    sorting: sorting,
    setSorting: setSorting
  });

  const renderImpersonateModal = () => {
    if (showImpersonateModal.show) {
      return (
        <DeleteModel
          onCancel={() => setShowImpersonateModal({ user_id: 0, show: false })}
          onConfirm={() => impersonateUser(showImpersonateModal.user_id)}
          title={capitalizeFirstLetter(t('user.general.text_1'))}
        />
      );
    }
  };

  console.log(process.env.REACT_APP_API_URL);

  return (
    <>
      <div className="d-flex justify-content-between mb-2 align-items-center flex-column flex-md-row">
        <div className="w-auto">
          <h3>{capitalizeFirstLetter(t('header.text.text_2'))} </h3>
        </div>
        <div className="d-flex gap-2 w-auto flex-column flex-md-row">
          <SearchBox
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <StandardSelect
            formClassName="w-330"
            id=""
            label={''}
            onChange={(e: string | number) => setSelectedUserType(e as string)}
            options={user_type}
            placeholder={t('user.button.text_3')}
            showError={false}
            validations={[]}
            value={selectedUserType}
            isClearable={true}
          />
          <ConditionalRender
            condition={checkAccess([modules.USERS.create], userPermission)}
          >
            <StandardButton
              buttonText={capitalizeFirstLetter(t('user.button.text_1'))}
              onClick={() => {
                navigate(USER_FORM);
              }}
            />
          </ConditionalRender>
        </div>
      </div>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <div className="px-4 py-1 px-lg-6 bg-body-emphasis  rounded border-top border-bottom border-left border-right border position-relative top-1">
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table fs-9 p-0',
                responsive: true
              }}
              dataLength={userList.length}
            />

            <ConditionalRender condition={totalPages > 0}>
              <CustomAdvanceTableFooter
                pagination
                totalPages={totalPages}
                totalRecords={totalRecords}
                recordsPerPage={limit}
                currentPage={pageCount}
                setCurrentPage={setPageCount}
                CanPreviousPage={() => pageCount > 1}
                CanNextPage={() => totalPages > pageCount}
                handleNextPage={() => setPageCount(pageCount + 1)}
                handlePreviousPage={() => setPageCount(pageCount - 1)}
                showViewAllBtn={true}
                isAllVisibleData={isAllVisibleData}
                setIsAllVisibleData={setIsAllVisibleData}
                showDataPerPage={totalPages > 1}
                setRecordsPerPage={setLimit}
                isLoading={isLoading}
              />
            </ConditionalRender>
          </AdvanceTableProvider>
        </div>
      </ConditionalRender>
      {renderImpersonateModal()}
    </>
  );
};

export default User;
